import { useEffect, useState } from 'react';
import { useLocale, type Option } from 'shared-ui';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { useModalState } from 'hooks/useModalState/useModalState';
import { AppMessages } from 'i18n/messages';
import { downloadOwnersWithoutEventsReport } from 'api/reports/reports';
import { useReportDownload } from '../hooks/useReportDownload';
import { useSmtpConfiguration } from '../hooks/useSmtpConfiguration';
import { EmailSelectionModalContainer } from '../emailSelectionModal';

import { useOwnersWithoutEventsReportValidation } from './OwnersWithoutEventsReport.validation';
import { OwnersWithoutEventsReportForm } from './OwnersWithoutEventsReport.types';
import { OwnersWithoutEventsReport } from './OwnersWithoutEventsReport';

export const OwnersWithoutEventsReportContainer = () => {
  const { isModalOpen, onModalClose, onModalOpen } = useModalState();
  const {
    isModalOpen: isEmailModalOpen,
    onModalClose: onEmailModalClose,
    onModalOpen: onEmailModalOpen,
  } = useModalState();
  const { formatMessage } = useLocale();
  const [selectedEmails, setSelectedEmails] = useState<Option[]>([]);
  const { isSmtpConfigured } = useSmtpConfiguration();

  const { schema } = useOwnersWithoutEventsReportValidation();
  const formMethods = useForm<OwnersWithoutEventsReportForm>({
    resolver: yupResolver(schema),
    mode: 'all',
  });

  const { nazwisko, numerKarty, dataOd, dataDo, komorka } = formMethods.watch();

  const {
    onDownload: onDownloadOwnersWithoutEventsReport,
    isLoading: isLoadingDownload,
    hasNoDataError,
    resetError,
  } = useReportDownload({
    mutationFn: downloadOwnersWithoutEventsReport,
    buildRequestBody: (emails) => ({
      nazwisko,
      numerKarty,
      dataOd: dataOd?.toISOString(),
      dataDo: dataDo?.toISOString(),
      komorki: komorka?.id ? [komorka?.id] : [],
      selectedEmails: emails,
    }),
    onEmailModalClose,
    reportNameId: AppMessages['reports.ownersWithoutEventsReport.title'],
  });

  const handleEmailModalOpen = () => {
    resetError();
    onEmailModalOpen();
  };

  const handleConfirmEmails = (emails: string[]) => {
    // Save selected emails for persistence
    setSelectedEmails(emails.map((email) => ({ value: email, label: email })));
    // Trigger the download/send
    onDownloadOwnersWithoutEventsReport(emails);
  };

  useEffect(() => {
    if (!isModalOpen) {
      // Reset form, error, and selected emails when modal closes
      formMethods.reset();
      resetError();
      setSelectedEmails([]);
    }
  }, [isModalOpen]);

  return (
    <FormProvider {...formMethods}>
      <OwnersWithoutEventsReport
        isModalOpen={isModalOpen}
        onModalClose={onModalClose}
        onModalOpen={onModalOpen}
        onDownload={onDownloadOwnersWithoutEventsReport}
        isLoadingDownload={isLoadingDownload}
        hasNoDataError={hasNoDataError}
        onEmailModalOpen={handleEmailModalOpen}
        isEmailModalOpen={isEmailModalOpen}
        isSmtpConfigured={isSmtpConfigured}
        onResetError={resetError}
      />
      {isModalOpen && (
        <EmailSelectionModalContainer
          title={formatMessage({ id: AppMessages['reports.ownersWithoutEventsReport.title'] })}
          open={isEmailModalOpen}
          onClose={onEmailModalClose}
          onConfirm={handleConfirmEmails}
          hasNoDataError={hasNoDataError}
          onResetError={resetError}
          selectedUsers={selectedEmails}
        />
      )}
    </FormProvider>
  );
};
